import { useCallback, useEffect } from 'react';
import { logger } from '../lib/logger';
import { useUserControlsContext } from '../contexts/UserControlsContext';
import { useApi } from '../lib/api';
import { submitImpersonationMapping } from '../api/admin';
import { rememberRecentUser, type RecentImpersonationUser } from '../lib/impersonation-recents';
import { useImpersonateWriteModeStore } from '../lib/impersonate-write-mode';
import { isProduction } from '../lib/environment';
import { claimImpersonationTrigger } from '../lib/impersonation-trigger-store';
import type { DemoOption } from '@shared/schemas/admin-access';
import { STOP_IMPERSONATING, type ImpersonationCommand } from '@shared/schemas/impersonation';

export { STOP_IMPERSONATING };
// Endpoint vocabulary lives with the endpoint; re-exported so existing
// impersonation call sites keep one import.
export { describeImpersonationError } from '../api/admin';
export type { ImpersonationCommand, ImpersonationTarget } from '@shared/schemas/impersonation';

/**
 * Unified impersonation hook.
 *
 * The server is the single source of truth for impersonation state.
 * The URL param `?impersonate=SLACK_ID` is a *trigger* — it creates a
 * server-side delegation, then strips itself from the URL. After that,
 * all state comes from the server's /api/me response.
 */
export function useImpersonation() {
  const { impersonation } = useUserControlsContext();
  const submitMapping = useSubmitImpersonationMapping();

  // If ?impersonate=X is in the URL, create the server-side delegation and reload.
  // After reload, the param is gone and /api/me has the delegation.
  // The claim is module-scope (see impersonation-trigger-store), so exactly one
  // mounted instance fires, across every component that mounts this hook and
  // across StrictMode's unmount/remount cycle.
  useEffect(() => {
    if (isProduction()) return;
    const url = new URL(window.location.href);
    const slackId = url.searchParams.get('impersonate');
    if (!slackId) return;
    if (!claimImpersonationTrigger(slackId)) return;

    // Already impersonating this user — just strip param
    if (impersonation?.active && impersonation.actingAsSlackId === slackId) {
      url.searchParams.delete('impersonate');
      window.history.replaceState(window.history.state, '', url.toString());
      return;
    }

    // Create delegation and reload
    submitMapping({ action: 'start', target: { slackUserId: slackId } }).then(() => {
      url.searchParams.delete('impersonate');
      window.location.replace(url.toString());
    }).catch(err => logger.error('[impersonation] Error:', err));
  }, [submitMapping, impersonation?.active, impersonation?.actingAsSlackId]); // the trigger claim guards against re-entry; deps listed for lint correctness

  return {
    active: !!impersonation?.active,
    actingAsId: impersonation?.actingAsSlackId ?? null,
    actingAsName: impersonation?.actingAs ?? null,
    cacheKey: impersonation?.actingAsSlackId ?? null,
    canUnlockWrites: impersonation?.canUnlockWrites === true,
    target: impersonation?.target ?? null,
  };
}

/**
 * POST /api/admin/me/mapping. The argument IS the server's command — nothing
 * here shapes the wire, so a change to the schema breaks this build rather than
 * a user's switch.
 *
 * Callers choose the navigation that resets their surrounding UI afterwards.
 * Switching to a known person goes through `useSwitchImpersonation` below, which
 * also feeds the recents list; this one is for stops and for the two callers
 * that hold a single identifier (the admin page has only an owner email, the
 * `?impersonate=` trigger only a Slack id). It used to take the recents-cache
 * entry — needing both — which is precisely why those two, and a fourth caller
 * in Delphi, hand-encoded the body instead, and why Delphi's went stale
 * unnoticed (2026-09-17, see docs/impersonation.md).
 */
export function useSubmitImpersonationMapping() {
  const { apiFetch } = useApi();

  return useCallback(async (command: ImpersonationCommand) => {
    await submitImpersonationMapping(apiFetch, command);
    // Only after the stop actually landed. Dropping the unlock first left a
    // failed stop with the session still live and writes silently off — every
    // later request omitted `x-impersonate-write` until a reload.
    if (command.action === 'stop') {
      useImpersonateWriteModeStore.getState().disable();
    }
  }, [apiFetch]);
}

/**
 * Switch to someone the caller has a full record for: the mapping, then the
 * shared recents list. Every picker holds that pair, and doing it in two lines
 * per picker is how the fourth one forgets the second.
 */
export function useSwitchImpersonation() {
  const submitMapping = useSubmitImpersonationMapping();

  return useCallback(async (user: RecentImpersonationUser) => {
    await submitMapping({ action: 'start', target: { email: user.ownerEmail } });
    rememberRecentUser(user);
  }, [submitMapping]);
}

/**
 * Start a demo session as a seeded demo identity. The Slack id is the stable
 * key. Unlike `useSwitchImpersonation` this never feeds the recents list: a
 * demo identity is not a colleague and must not reappear in Quick or Cmd+K.
 */
export function useStartDemo() {
  const submitMapping = useSubmitImpersonationMapping();

  return useCallback(
    (option: Pick<DemoOption, 'slackUserId'>) => submitMapping({ action: 'start', target: { slackUserId: option.slackUserId } }),
    [submitMapping],
  );
}
